import { For, createResource, createSignal } from "solid-js";
import { A } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import PageHeader from "../components/PageHeader";
import CursorPager from "../components/CursorPager";
import { needsFirm } from "../components/FirmPicker";
import { api, type MemberListCursor } from "../lib/api";
import { createCursorPages } from "../lib/cursorPages";
import { useSession } from "../lib/session";

const PAGE_SIZE = 50;

export default function UsersList() {
  const session = useSession();
  const pages = createCursorPages<MemberListCursor>();
  const [emailInput, setEmailInput] = createSignal("");
  const [emailFilter, setEmailFilter] = createSignal<string | null>(null);

  const [page] = createResource(
    () =>
      !needsFirm(session.admin()?.role ?? "") && {
        cursor: pages.current(),
        email: emailFilter(),
      },
    ({ cursor, email }) =>
      api.listMembers({
        limit: PAGE_SIZE,
        email: email ?? undefined,
        ...cursor,
      }),
  );

  return (
    <>
      <PageHeader title="Members" subtitle="The firm's traders and admins" />

      <div class="card mb-4">
        <div class="card-body">
          <form
            class="flex gap-2 items-end"
            onSubmit={(e) => {
              e.preventDefault();
              pages.reset();
              setEmailFilter(emailInput().trim() || null);
            }}
          >
            <div class="flex-1 max-w-sm">
              <label class="label" for="email-filter">
                Email contains
              </label>
              <input
                id="email-filter"
                class="input"
                value={emailInput()}
                onInput={(e) => setEmailInput(e.currentTarget.value)}
                placeholder="e.g. @example.com"
              />
            </div>
            <button type="submit" class="btn-primary">
              Search
            </button>
            <button
              type="button"
              class="btn-secondary"
              onClick={() => {
                setEmailInput("");
                setEmailFilter(null);
                pages.reset();
              }}
            >
              Clear
            </button>
          </form>
        </div>
      </div>

      <div class="card">
        <AsyncResource resource={page}>
          {(data) => (
            <>
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={data().members}>
                    {(u) => (
                      <tr>
                        <td class="font-mono text-xs">{u.email ?? "—"}</td>
                        <td>{u.display_name ?? "—"}</td>
                        <td>{u.role}</td>
                        <td>
                          <span
                            class={
                              u.is_active ? "status-active" : "status-neutral"
                            }
                          >
                            {u.is_active ? "active" : "disabled"}
                          </span>
                        </td>
                        <td class="text-slate-500">
                          {new Date(u.created_at).toLocaleString()}
                        </td>
                        <td>
                          <A
                            class="text-sm text-slate-700 hover:text-slate-900 underline"
                            href={`/users/${u.global_user_id}`}
                          >
                            View
                          </A>
                        </td>
                      </tr>
                    )}
                  </For>
                  {data().members.length === 0 && (
                    <tr>
                      <td colSpan={6} class="text-center text-slate-500 py-6">
                        No members match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div class="card-body">
                <CursorPager pages={pages} next={data().next_cursor} />
              </div>
            </>
          )}
        </AsyncResource>
      </div>
    </>
  );
}
